import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AccessControlService } from '../access-control/access-control.service';
import { OnboardingStateService } from '../onboarding/onboarding-state.service';
import { SharedSession, SharedSessionResponse } from './shared-session.models';
import { clearSharedSession, consumeHandoffHints, readSessionToken, writeSessionToken } from './shared-session-cookie';

/** POST — the shared-session token (as Authorization) → currentUser/workspace/project. */
const AUTH_ME_ENDPOINT = 'api/v2/auth/me';

/** The shared-session token this app's localStorage session was last reconciled with. */
const SYNCED_TOKEN_KEY = 'sharedSessionToken';

/**
 * Keeps this app's localStorage session in line with the cross-app session cookie
 * (shared-session-cookie.ts). Runs once on every page load, before routing (APP_INITIALIZER):
 *
 *  - Cookie and local session match → nothing to do.
 *  - Cookie changed elsewhere (another app signed someone in) → restore the session from
 *    POST api/v2/auth/me, replacing whatever this app had.
 *  - Cookie gone but this app had synced it → the user signed out in another app → sign out here.
 *  - This app signed in on its own since the last sync (its own login pages, or a session from
 *    before the shared session existed) → publish its token to the cookie for the other apps.
 *
 * A rejected token (401/403) signs the browser out everywhere. Any other failure (network,
 * endpoint missing) leaves things as they are — AuthGuardService then sends a signed-out user to
 * /login as usual.
 */
@Injectable({
    providedIn: 'root',
})
export class SharedSessionService {
    // Built on HttpBackend so no interceptor touches this call: TokenInterceptor would replace the
    // token being checked with localStorage.token, and ErrorInterceptorService clears localStorage
    // on a 401.
    private readonly http = new HttpClient(inject(HttpBackend));
    private readonly accessControl = inject(AccessControlService);
    private readonly onboardingState = inject(OnboardingStateService);

    async syncOnStartup(): Promise<void> {
        const cookieToken = readSessionToken();
        const localToken = localStorage.getItem('token');
        const hasLocalSession = localStorage.getItem('currentUser') !== null && !!localToken;
        const syncedToken = localStorage.getItem(SYNCED_TOKEN_KEY);
        const signedInHereSinceLastSync = hasLocalSession && syncedToken !== localToken;

        if (signedInHereSinceLastSync && cookieToken !== localToken) {
            this.publish(localToken!);
            return;
        }

        if (!cookieToken) {
            if (hasLocalSession) {
                clearLocalSession(); // Signed out in another app.
            }
            return;
        }

        if (hasLocalSession && cookieToken === localToken) {
            localStorage.setItem(SYNCED_TOKEN_KEY, cookieToken);
            this.applyHandoffHints();
            return;
        }

        try {
            const session = await firstValueFrom(this.fetchSession(cookieToken));
            await this.restore(session);
        } catch (error) {
            const status = (error as HttpErrorResponse)?.status;
            if (status === 401 || status === 403) {
                clearSharedSession();
                clearLocalSession();
            }
        }
    }

    fetchSession(token: string): Observable<SharedSession> {
        return this.http
            .post<SharedSessionResponse>(
                environment.baseApiUrl + AUTH_ME_ENDPOINT,
                {},
                { headers: new HttpHeaders({ Authorization: token }) }
            )
            .pipe(
                map((response) => ({
                    token,
                    user: response.currentUser,
                    isNewUser: !!response.isNewUser,
                    onboardingStep: response.onboardingStep ?? null,
                    workspace: response.lastVisited_workspace ?? null,
                    project: response.selectedProject ?? null,
                    firebaseToken: response.firebaseToken ?? null,
                }))
            );
    }

    private publish(token: string): void {
        writeSessionToken(token);
        localStorage.setItem(SYNCED_TOKEN_KEY, token);
    }

    /** Same localStorage writes as fusion-onboarding's OnboardingHandoffService. */
    private async restore(session: SharedSession): Promise<void> {
        clearLocalSession(); // Whatever was here belonged to a different session.

        localStorage.setItem('token', session.token);
        localStorage.setItem('currentUser', JSON.stringify(session.user));
        if (session.firebaseToken) {
            localStorage.setItem('firebaseToken', session.firebaseToken);
        }
        if (session.workspace) {
            localStorage.setItem('lastVisited_workspace', JSON.stringify(session.workspace));
        }
        if (session.project) {
            localStorage.setItem('selectedProject', JSON.stringify(session.project));
        }
        this.restoreOnboardingStep(session.onboardingStep);
        if (session.isNewUser) {
            localStorage.setItem('isNewOnboardedUser', 'true'); // TourGuideService's flag
        }
        this.publish(session.token);
        this.applyHandoffHints();

        if (session.workspace?.ws_kuid && session.project?.p_kuid) {
            await this.loadProjectRoleAccess(session.workspace.ws_kuid, session.project.p_kuid);
        }
    }

    /**
     * The backend's step for the old /get-started flow. Only honored while that flow is live —
     * with the new onboarding app rolled out (environment.newOnboarding), /get-started/* redirects
     * there and the new app sends a signed-in user straight back, so a pending step would loop.
     */
    private restoreOnboardingStep(step: string | null): void {
        if (step && step !== 'completed' && !environment.newOnboarding.enabled) {
            localStorage.setItem('onboardingStep', step);
        } else {
            this.onboardingState.markCompleted();
        }
    }

    private applyHandoffHints(): void {
        const hints = consumeHandoffHints();
        if (hints.isNewOnboardedUser) {
            localStorage.setItem('isNewOnboardedUser', 'true');
        }
        if (hints.isViewOnlyMode) {
            localStorage.setItem('isViewOnlyMode', 'true');
        }
    }

    /** Same call ProjectTileComponent makes before opening a project — the dashboard's permission
     *  checks read localStorage.userProjectRole. Best effort: failing it must not block startup. */
    private async loadProjectRoleAccess(workspaceKuid: string, projectKuid: string): Promise<void> {
        const body = { payload: JSON.stringify({ workspace_kuid: workspaceKuid, project_kuid: projectKuid }) };
        try {
            const res = await firstValueFrom(this.accessControl.projectRoleAccess(body));
            if (res?.userProjectRole) {
                localStorage.setItem('userProjectRole', JSON.stringify(res));
            }
        } catch {
            // Permissions load later when the user opens a project.
        }
    }
}

/** Same reset the app's logout handlers do (keeping the rememberMe preference). */
function clearLocalSession(): void {
    const rememberMe = localStorage.getItem('rememberMe');
    localStorage.clear();
    if (rememberMe) {
        localStorage.setItem('rememberMe', rememberMe);
    }
}
